import React, { useState } from 'react';
import { auth } from '../../../shared/storage/firebase';

interface CreateSquadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSquadCreated: (newSquad: any) => void;
  locations: Array<{ id: string; name: string; partnerId: string }>;
}

const CreateSquadModal: React.FC<CreateSquadModalProps> = ({ isOpen, onClose, onSquadCreated, locations }) => {
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [locationId, setLocationId] = useState('');
  const [seasonId, setSeasonId] = useState('');
  const [deliveryMode, setDeliveryMode] = useState<'in_person' | 'online'>('in_person');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsCreating(true);

    if (!locationId || !seasonId.trim()) {
      setError('Location and season are required.');
      setIsCreating(false);
      return;
    }

    if (!/^[a-zA-Z0-9]{5}$/.test(code)) {
      setError("Code must be 5 alphanumeric characters.");
      setIsCreating(false);
      return;
    }

    try {
      const selectedLocation = locations.find((location) => location.id === locationId);
      if (!selectedLocation?.partnerId) throw new Error('Selected location has no canonical partner.');
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Sign in is required.');
      const projectId = auth.app.options.projectId;
      const response = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/createSquad`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          code,
          curriculumId: 'intro-ai-kids-2026',
          partnerId: selectedLocation.partnerId,
          locationId: selectedLocation.id,
          seasonId: seasonId.trim(),
          deliveryMode,
        }),
      });
      if (!response.ok) throw new Error(await response.text());
      onSquadCreated(await response.json());
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to create squad.");
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Create New Squad</h2>
        <p style={{ marginTop: 6, marginBottom: 14, color: '#6b7280', fontSize: 14 }}>
          Squads require a canonical partner location and season.
        </p>
        <form onSubmit={handleCreate}>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            required
            style={{ width: '100%', marginBottom: 10 }}
          >
            <option value="">Select a location…</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Season ID (e.g. fall2026)"
            value={seasonId}
            onChange={(e) => setSeasonId(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Squad Title (e.g., 'Period 3 - Jazz Band')"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={100}
          />
          <select
            value={deliveryMode}
            onChange={(e) => setDeliveryMode((e.target.value as any) || 'in_person')}
            style={{ width: '100%', marginBottom: 10 }}
          >
            <option value="in_person">In-person</option>
            <option value="online">Online</option>
          </select>
          <input
            type="text"
            placeholder="5-Character Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            maxLength={5}
          />
          {error && <p className="error-message">{error}</p>}
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={isCreating}>Cancel</button>
            <button type="submit" disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create Squad'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSquadModal; 
